<template>
  <main class="main main--has-floating-buttons">
    <portal to="headerTitle">
      {{ $t('admin.interfaces.pageOnlineBooking.title') }}
    </portal>
    <template v-if="config !== null">
      <card :title="$t('admin.interfaces.pageOnlineBooking.card.general')" cols="12 6@desktop" rows="2">
        <!--        <card-badge :tooltip="$t('betaVersion.tooltip')">{{ $t('betaVersion.title') }}</card-badge>-->
        <hm-card-inner v-if="hotelSettings.details.google" full-size>
          <hm-col cols="12">
            <span class="info-text info-text--full-size info-text--no-margin col-12">{{ $t('admin.interfaces.pageOnlineBooking.infoTextGoogle') }}</span>
          </hm-col>
        </hm-card-inner>
        <hm-card-inner v-if="hasRole('ROLE_SYSADMIN') && !isNil(hotelSettings.featureToggles) && hotelSettings.featureToggles.GoogleHotel !== true" full-size>
          <hm-col cols="12">
            <span class="warning-text warning-text--full-size warning-text--no-margin col-12">Google Hotel is <b>DISABLED</b> globally. In case you do have any questions, please contact support.</span>
          </hm-col>
        </hm-card-inner>
        <hm-card-inner>
          <hm-input-toggle :label="$t('admin.interfaces.pageOnlineBooking.enabled')"
                           :enabled-title="`${$t('enabled')} 🔒`"
                           :disabled-title="$t('disabled')"
                           v-model="config.enabled"
                           :disabled="config.enabled && !hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN') && hotelSettings.details.google"
                           cols="12 6@hd start-1"
                           name="enabled" />
          <hm-input-toggle v-if="hasRole('ROLE_SYSADMIN')"
                           :label="$t('admin.interfaces.pageOnlineBooking.googleHotelEnabledLabel')"
                           :enabled-title="`${$t('googleHotelEnabled')} 🔒`"
                           :disabled-title="`${$t('googleHotelDisabled')} 🔒`"
                           v-model="config.googleHotelEnabled"
                           :disabled="!config.enabled || !hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')"
                           cols="12 6@hd start-1"
                           name="googleHotelEnabled" />
          <hm-input-toggle v-if="hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')"
                           v-model="config.googleHotelCreated"
                           :disabled="!config.enabled || !config.googleHotelEnabled || !hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')"
                           label="Do not set, unless existance of hotel was confirmed in Google Hotel"
                           enabled-title="Google Hotel: Created 🔒"
                           disabled-title="Google Hotel: Not created 🔒"
                           cols="12 6@hd start-1"
                           name="googleHotelEnabled" />
          <hm-input v-model="config.gtagId"
                    :lazy="true"
                    :label="$t('admin.interfaces.pageOnlineBooking.gtagId')"
                    name="title"
                    cols="12 6@hd start-1" />
        </hm-card-inner>
        <!-- TODO: remove debug output, once we confirmed google is working as expected -->
        <hm-card-inner v-if="hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')" title="Google Hotel Data 🔒">
          <hm-col cols="12">
            <pre v-html="config.googleHotelData" />
          </hm-col>
        </hm-card-inner>
        <hm-card-inner ref="code" :full-size="true">
          <b style="padding-top:1.5rem;padding-left:1.5rem;">Code</b>
          <pre class="col-12" style="font-family: monospace;font-size: 1.4rem;white-space: pre-line;cursor: copy;margin:0;padding: 1.5rem;" @click="copyCode">
            &lt;script src="https://hmwidgets.web.app/shadow/js/app.js" type="application/javascript"&gt;&lt;/script&gt;
            &lt;book-now-button hotel="{{ $route.params.hotelId }}" label="Jetzt Buchen!" lang="de" env="{{ isProduction() ? 'production' : 'staging' }}"&gt;&lt;/book-now-button&gt;
          </pre>
          <a v-if="['Hotelmeister', 'Digibooking'].includes(hotelSettings.hotel.whiteLabel)" style="padding-left: 1.5rem;padding-bottom: 1.5rem;" href="https://apidocs.hotelmeister.io/booking-engine" target="_blank">Dokumentation</a>
        </hm-card-inner>
        <hm-card-inner :title="$t('admin.interfaces.pageOnlineBooking.card.appearance')">
          <hm-color-picker :label="$t('admin.interfaces.pageOnlineBooking.primaryColor')"
                           v-model="config.colors.primaryColor"
                           cols="12 6@hd start-1"
                           name="primaryColor" />
          <hm-input-select v-model="config.roomSelectionDisplay"
                           :label="$t('admin.interfaces.pageOnlineBooking.roomSelectionDisplay')"
                           cols="12 6@hd start-1"
                           name="roomSelectionDisplay"
                           enum-name="OnlineBookingRoomSelectionDisplayMode" />
          <hm-input-select v-model="config.wordingType"
                           :label="$t('admin.interfaces.pageOnlineBooking.wordingType')"
                           cols="12 6@hd start-1"
                           name="roomSelectionDisplay"
                           enum-name="AccommodationWordingType" />
        </hm-card-inner>
        <hm-card-inner v-if="config.googleHotelEnabled" title="Google">
          <hm-input-toggle :label="$t('admin.interfaces.pageOnlineBooking.includingAllTaxesAndFees')"
                           v-model="config.includingAllTaxesAndFees"
                           cols="12 6@hd start-1"
                           name="includingAllTaxesAndFees" />
          <hm-input-multilang v-if="config.includingAllTaxesAndFees !== true"
                              v-model="config.additionalTaxesAndFees"
                              :lazy="true"
                              :label="$t('admin.interfaces.pageOnlineBooking.additionalTaxesAndFees')"
                              name="additionalTaxesAndFees" />
        </hm-card-inner>
        <hm-card-inner :title="$t('admin.interfaces.pageOnlineBooking.card.experimentalFeatures')">
          <hm-input-toggle :label="$t('admin.interfaces.pageOnlineBooking.extendedAriValidation')"
                           :help="$t('admin.interfaces.pageOnlineBooking.extendedAriValidation_help')"
                           v-model="config.extendedAriValidation"
                           cols="12 6@hd start-1"
                           name="extendedAriValidation" />
          <hm-input-toggle :label="$t('admin.interfaces.pageOnlineBooking.ariMinOccupancyRate')"
                           :help="$t('admin.interfaces.pageOnlineBooking.ariMinOccupancyRate_help')"
                           v-model="config.ariMinOccupancyRate"
                           cols="12 6@hd start-1"
                           name="ariMinOccupancyRate" />
          <hm-input-toggle :label="$t('admin.interfaces.pageOnlineBooking.displayMinRate')"
                           v-model="config.displayMinRate"
                           cols="12 6@hd start-1"
                           name="displayMinRate" />
          <hm-input-toggle v-if="hasLicense('DiscountCode')"
                           :label="$t('admin.interfaces.pageOnlineBooking.discountCodes')"
                           :help="$t('admin.interfaces.pageOnlineBooking.discountCodes_help')"
                           v-model="config.discountCodes"
                           cols="12 6@hd start-1"
                           name="discountCodes" />
        </hm-card-inner>
        <hm-card-inner :title="$t('admin.interfaces.pageOnlineBooking.card.confirmationPage')">
          <!--          <hm-input-multilang v-model="config.preCheckInUrl"-->
          <!--                              :lazy="true"-->
          <!--                              :label="$t('admin.interfaces.pageOnlineBooking.preCheckInUrl')" />-->
          <hm-input-multilang v-model="config.websiteUrl"
                              :lazy="true"
                              :label="$t('admin.interfaces.pageOnlineBooking.websiteUrl')" />
          <hm-input-multilang v-model="config.regionInformationUrl"
                              :lazy="true"
                              :label="$t('admin.interfaces.pageOnlineBooking.regionInformationUrl')" />

        </hm-card-inner>
        <hm-card-inner :title="$t('admin.interfaces.pageOnlineBooking.card.confirmationEmail')">
          <hm-input-select id="defaultReservationConfirmationTemplateId"
                           v-model="config.defaultReservationConfirmationTemplateId"
                           :label="$t('admin.interfaces.pageOnlineBooking.defaultReservationConfirmationTemplateId')"
                           name="defaultReservationConfirmationTemplateId"
                           cols="12 6@hd start-1">
            <option v-if="templates.length <= 1" :value="null"></option>
            <option v-for="template in templates" :value="template.id">{{ template.name }}</option>
          </hm-input-select>
        </hm-card-inner>
      </card>
      <card :title="$t('admin.interfaces.pageOnlineBooking.card.payments')" cols="12 6@desktop">
        <hm-card-inner v-if="config.paymentConfiguration.primaryPaymentServiceProvider !== 'Manual'" :full-size="true">
          <hm-col cols="12">
            <span class="info-text info-text--full-size info-text--no-margin col-12">{{ $t('admin.interfaces.pageOnlineBooking.infoTextEnablePaymentMethodsAtPspFirst') }}</span>
          </hm-col>
        </hm-card-inner>
        <hm-card-inner v-else :full-size="true">
          <hm-col cols="12">
            <span class="info-text info-text--full-size info-text--no-margin col-12">{{ $t('admin.interfaces.pageOnlineBooking.infoTextPleaseConnectPspToPMS') }}</span>
          </hm-col>
        </hm-card-inner>
        <hm-card-inner>
          <hm-input-select v-model="config.prepaymentIhsId"
                           :label="$t('admin.pageHotelSettings.onlineBookingPrepaymentIhsId')"
                           cols="12 6@hd start-1">
            <option :value="null"></option>
            <option v-for="item in hotelSettings.inHouseServices.all(config.prepaymentIhsId).filter(it => it.type === 'PrePayment')" :value="item.id" v-html="item.name[$shortLocale]"></option>
          </hm-input-select>
        </hm-card-inner>
        <hm-card-inner>
          <hm-input-toggle v-model="config.paymentConfiguration.allowManualPaymentAsGuarantee"
                           :label="$t('admin.interfaces.pageOnlineBooking.allowManualPaymentAsGuarantee')"
                           :disabled="paymentMethodConfigMap.Manual.Other.active !== true"
                           cols="12 6@hd start-1"
                           name="allowManualPaymentAsGuarantee"/>
          <hm-input-select v-model="config.paymentConfiguration.primaryPaymentServiceProvider"
                           :label="$t('admin.interfaces.pageOnlineBooking.paymentServiceProvider')"
                           :disabled-options="disabledPaymentServiceProviders"
                           cols="12 6@hd start-1"
                           name="primaryPaymentServiceProvider"
                           enum-name="PaymentServiceProvider" />
        </hm-card-inner>
        <hm-card-inner>
          <hm-input-select id="reservationRegisteredTemplateId"
                           v-model="config.reservationRegisteredTemplateId"
                           :label="$t('admin.interfaces.pageOnlineBooking.reservationRegisteredTemplateId')"
                           name="reservationRegisteredTemplateId"
                           cols="12 6@hd start-1">
            <option :value="null"></option>
            <option v-for="template in genericEmailTemplates" :value="template.id">{{ template.name }}</option>
          </hm-input-select>
          <hm-button cols="12 6@tablet 3@hd" class="btn--gray" icon-left="refresh" @click="updateTemplates">{{ $t('admin.interfaces.pageOnlineBooking.reloadTemplates') }}</hm-button>
          <hm-button cols="12 6@tablet 3@hd" class="btn--blue" icon-left="plus" @click="createDefaultPaymentRegisteredTemplate">{{ $t('admin.interfaces.pageOnlineBooking.addDefaultPaymentRegisteredTemplate') }}</hm-button>
        </hm-card-inner>
      </card>
      <tabable-card rows="4" cols="12 6@desktop start-7@desktop">
        <template v-for="paymentServiceProvider in enums.PaymentServiceProvider">
          <tabable-card-content :title="$t(`PaymentServiceProvider.${paymentServiceProvider}`)" :key="paymentServiceProvider" :display="!disabledPaymentServiceProviders.includes(paymentServiceProvider)">
            <template v-for="paymentMethod in enums.PaymentServiceProviderPaymentMethod">
              <hm-card-inner v-if="enums.ADDITIONAL_DATA.PaymentServiceProviderPaymentMethod[paymentMethod].paymentServiceProviders.includes(paymentServiceProvider)"
                             :key="paymentMethod"
                             :title="$t(`PaymentServiceProviderPaymentMethod.${paymentMethod}`)">
                <hm-input-toggle :label="$t('admin.interfaces.pageOnlineBooking.paymentMethodEnabled')"
                                 :enabled-title="$t('enabled')"
                                 :disabled-title="$t('disabled')"
                                 v-model="paymentMethodConfigMap[paymentServiceProvider][paymentMethod].active"
                                 :disabled="!enums.ADDITIONAL_DATA.PaymentServiceProviderPaymentMethod[paymentMethod].enabled && !hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')"
                                 cols="12 start-1"
                                 name="paymentMethodEnabled" />
                <hm-input-range :min="0"
                                :max="14"
                                :step="1"
                                :value-map="{ 0: 'Immer' }"
                                v-model="paymentMethodConfigMap[paymentServiceProvider][paymentMethod].minDaysBeforeArrival"
                                :disabled="!enums.ADDITIONAL_DATA.PaymentServiceProviderPaymentMethod[paymentMethod].enabled && !hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')"
                                :label="$t('admin.interfaces.pageOnlineBooking.paymentMethodMinDaysBeforeArrival')"
                                name="lowAvailabilityMessageThreshold" />
              </hm-card-inner>
            </template>
          </tabable-card-content>
        </template>
      </tabable-card>
      <card :title="$t('admin.interfaces.pageOnlineBooking.card.other')" cols="12 6@desktop" rows="1">
        <hm-card-inner>
          <hm-input-toggle :label="$t('admin.interfaces.pageOnlineBooking.hideBusinessTermsCheckbox')"
                           v-model="config.hideBusinessTermsCheckbox"
                           cols="12 start-1"
                           name="hideBusinessTermsCheckbox" />
          <hm-input-toggle :label="$t('admin.interfaces.pageOnlineBooking.showPrivacyCheckbox')"
                           v-model="config.showPrivacyCheckbox"
                           cols="12 start-1"
                           name="showPrivacyCheckbox" />
          <hm-input-toggle :label="$t('admin.interfaces.pageOnlineBooking.requireAddress')"
                           v-model="config.requireAddress"
                           cols="12 start-1"
                           name="requireAddress" />
          <hm-input-toggle :label="$t('admin.interfaces.pageOnlineBooking.requirePhoneNumber')"
                           v-model="config.requirePhoneNumber"
                           cols="12 start-1"
                           name="requirePhoneNumber" />
          <hm-input-toggle :label="$t('admin.interfaces.pageOnlineBooking.askForBillingAddress')"
                           v-model="config.askForCompanyName"
                           cols="12 start-1"
                           name="askForCompanyName" />
          <hm-input-range :min="0"
                          :max="hotelSettings.rooms.all().length"
                          :step="1"
                          :value-map="{ 0: 'Nie', [hotelSettings.rooms.all().length]: 'Immer' }"
                          v-model="config.lowAvailabilityMessageThreshold"
                          :label="$t('admin.interfaces.pageOnlineBooking.lowAvailabilityMessageThreshold')"
                          name="lowAvailabilityMessageThreshold" />
          <hm-input-select v-model="config.cutOffPeriod"
                           :label="$t('admin.interfaces.pageOnlineBooking.cutOffPeriod')"
                           name="cutOffPeriod">
            <option value="PT0S">{{ $t("LeadTimePeriod.PT0S") }}</option>
            <option value="PT2H">{{ $t("LeadTimePeriod.PT2H") }}</option>
            <option value="PT4H">{{ $t("LeadTimePeriod.PT4H") }}</option>
            <option value="PT6H">{{ $t("LeadTimePeriod.PT6H") }}</option>
            <option value="PT9H">{{ $t("LeadTimePeriod.PT9H") }}</option>
            <option value="P1D">{{ $t("LeadTimePeriod.P1D") }}</option>
            <option value="P1DT6H">{{ $t("LeadTimePeriod.P1DT6H") }}</option>
            <option value="P1DT9H">{{ $t("LeadTimePeriod.P1DT9H") }}</option>
            <option value="P1DT12H">{{ $t("LeadTimePeriod.P1DT12H") }}</option>
            <option value="P1DT15H">{{ $t("LeadTimePeriod.P1DT15H") }}</option>
            <option value="P2D">{{ $t("LeadTimePeriod.P2D") }}</option>
            <option value="P2DT6H">{{ $t("LeadTimePeriod.P2DT6H") }}</option>
            <option value="P2DT9H">{{ $t("LeadTimePeriod.P2DT9H") }}</option>
            <option value="P2DT15H">{{ $t("LeadTimePeriod.P2DT15H") }}</option>
            <option value="P3D">{{ $t("LeadTimePeriod.P3D") }}</option>
            <option value="P4D">{{ $t("LeadTimePeriod.P4D") }}</option>
            <option value="P5D">{{ $t("LeadTimePeriod.P5D") }}</option>
            <option value="P6D">{{ $t("LeadTimePeriod.P6D") }}</option>
            <option value="P1W">{{ $t("LeadTimePeriod.P1W") }}</option>
            <option value="P2W">{{ $t("LeadTimePeriod.P2W") }}</option>
            <option value="P3W">{{ $t("LeadTimePeriod.P3W") }}</option>
            <option value="P1M">{{ $t("LeadTimePeriod.P1M") }}</option>
            <option value="P2M">{{ $t("LeadTimePeriod.P2M") }}</option>
            <option value="P3M">{{ $t("LeadTimePeriod.P3M") }}</option>
            <option value="P4M">{{ $t("LeadTimePeriod.P4M") }}</option>
            <option value="P5M">{{ $t("LeadTimePeriod.P5M") }}</option>
            <option value="P6M">{{ $t("LeadTimePeriod.P6M") }}</option>
            <option value="P1Y">{{ $t("LeadTimePeriod.P1Y") }}</option>
          </hm-input-select>
          <hm-input-toggle :label="$t('admin.interfaces.pageOnlineBooking.hideNoPrepayment')"
                           :help="$t('admin.interfaces.pageOnlineBooking.hideNoPrepayment_help')"
                           v-model="config.hideNoPrepayment"
                           cols="12 start-1"
                           name="hideNoPrepayment" />
        </hm-card-inner>
      </card>

      <tabable-card cols="12 6@desktop">
        <tabable-card-content :title="$t(`lang.${hotelSettings.details.primaryLanguage}`)">
          <hm-card-inner :full-size="true">
            <hm-col cols="12">
              <span class="info-text info-text--full-size info-text--no-margin col-12" v-html="$t('admin.interfaces.pageOnlineBooking.textInfo')" />
            </hm-col>
          </hm-card-inner>
          <hm-card-inner :title="$t('admin.interfaces.pageOnlineBooking.card.imprint')">
            <hm-col cols="12">
              <ckeditor id="editor_imprint" v-model="config.imprintPageContent[hotelSettings.details.primaryLanguage]" :incard="false" height="150" />
            </hm-col>
          </hm-card-inner>
          <hm-card-inner :title="$t('admin.interfaces.pageOnlineBooking.card.confirmationPage')">
            <hm-col cols="12">
              <ckeditor id="editor_confirmationPage" v-model="config.reservationConfirmedPageContent[hotelSettings.details.primaryLanguage]" :incard="false" height="250" />
            </hm-col>
          </hm-card-inner>
          <!--
          <hm-card-inner title="Rechte Spalte (Zimmerauswahl)">
            <hm-col cols="12">
              <ckeditor id="editor_roomSelection" v-model="config.selectionCol2Content[hotelSettings.details.primaryLanguage]" :incard="true" height="250" />
            </hm-col>
          </hm-card-inner>
          <hm-card-inner title="Rechte Spalte (Kontaktdateneingabe)">
            <hm-col cols="12">
              <ckeditor id="editor_contactDataEntry" v-model="config.selectionCol2Content[hotelSettings.details.primaryLanguage]" :incard="true" height="250" />
            </hm-col>
          </hm-card-inner>
          -->
        </tabable-card-content>
        <tabable-card-content v-for="lang in hotelSettings.details.additionalLanguages" :title="$t(`lang.${lang}`)" :key="lang">
          <hm-card-inner :full-size="true">
            <hm-col cols="12">
              <span class="info-text info-text--full-size info-text--no-margin col-12" v-html="$t('admin.interfaces.pageOnlineBooking.textInfo')" />
            </hm-col>
          </hm-card-inner>
          <hm-card-inner :title="$t('admin.interfaces.pageOnlineBooking.card.imprint')">
            <hm-col cols="12">
              <ckeditor :id="`editor_imprint_${lang}`" v-model="config.imprintPageContent[lang]" :incard="false" height="150" />
            </hm-col>
          </hm-card-inner>
          <hm-card-inner :title="$t('admin.interfaces.pageOnlineBooking.card.confirmationPage')">
            <hm-col cols="12">
              <ckeditor :id="`editor_confirmationPage_${lang}`" v-model="config.reservationConfirmedPageContent[lang]" :incard="false" height="250" />
            </hm-col>
          </hm-card-inner>
        </tabable-card-content>
      </tabable-card>


      <hm-floating-buttons>
        <hm-floating-button v-if="config.googleHotelEnabled && hasRole('ROLE_SYSADMIN')"
                            :label="$t('admin.interfaces.pageOnlineBooking.actions.more')"
                            icon="fill-more"
                            color="gray">
          <hm-floating-child-button v-if="hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')"
                                    :throttle="15000"
                                    label="Google Hotel: Download Listings XML for ALL hotels 🔒"
                                    icon="download"
                                    color="orange"
                                    @click="googleHotelXml(true)" />
          <hm-floating-child-button v-if="hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')"
                                    :throttle="15000"
                                    label="Google Hotel: Download Listings XML 🔒"
                                    icon="download"
                                    color="orange"
                                    @click="googleHotelXml(false)" />
          <hm-floating-child-button :throttle="15000"
                                    label="Google Hotel: Update Property (Room Types & Rate Plans)"
                                    icon="upload"
                                    color="orange"
                                    @click="googleUpdateProperty" />
          <hm-floating-child-button :throttle="15000"
                                    label="Google Hotel: Update Taxes/Fees"
                                    icon="upload"
                                    color="orange"
                                    @click="googleUpdateTaxesFees" />
          <hm-floating-child-button :throttle="15000"
                                    label="Google Hotel: Update Rates & Availabilities"
                                    icon="upload"
                                    color="orange"
                                    @click="updateAllRatesAndAvailabilities" />
        </hm-floating-button>
        <hm-floating-button v-if="hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN') && env === 'production'"
                            :throttle="15000"
                            :label="`${$t('admin.interfaces.pageOnlineBooking.actions.invalidateCache')} 🔒`"
                            icon="refresh"
                            color="orange"
                            @click="invalidateCache" />
        <hm-floating-button v-if="!isEmpty(config.url)"
                            :throttle="1000"
                            :label="$t('admin.interfaces.pageOnlineBooking.actions.open')"
                            icon="eye"
                            color="green"
                            @click="openUrl(`${config.url}?debug=1`)" />
      </hm-floating-buttons>
    </template>
  </main>
</template>

<script>
/* eslint func-names: 0 */

import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import SaveWatchMixin from '@/mixins/SaveWatchMixin';
import Card from '../../../lib/layout/Card';
import HmInputCheckbox from '../../../lib/inputs/HmInputCheckbox';
import HmInputMoney from '../../../lib/inputs/HmInputMoney';
import HmCardInner from '../../../lib/layout/HmCardInner';
import HmTextarea from '../../../lib/inputs/HmTextarea';
import HmTable from '../../../lib/table/HmTable';
import { cloneDeep, groupBy, isEmpty, isNil, sortBy } from 'lodash';
import TabableCard from '../../../lib/layout/TabableCard';
import TabableCardContent from '../../../lib/layout/TabableCardContent';
import { TimelineMax, TweenMax } from 'gsap';
import { isProduction } from '@/lib/util/isProduction';
import HmInputSelect from '../../../lib/inputs/HmInputSelect';
import HmButton from '../../../lib/buttons/HmButton.vue';
import HmCol from '../../../lib/layout/HmCol.vue';
import HmFloatingButton from '../../../lib/inputs/HmFloatingButton.vue';
import HmFloatingChildButton from '../../../lib/inputs/HmFloatingChildButton.vue';

export default {
  name: 'PageOnlineBooking',
  components: { HmFloatingChildButton, HmFloatingButton, HmCol, HmButton, HmInputSelect, TabableCardContent, TabableCard, HmTable, HmTextarea, HmCardInner, HmInputMoney, HmInputCheckbox, Card },
  mixins: [HotelmeisterMixin, SaveWatchMixin],

  autosave: [
    {
      name: 'config',
      variable: 'config',
      event: 'hotelSettings.update',
      fn: function (cbSaveComplete) {
        const storeConfig = cloneDeep(this.config);
        try {
          const paymentMethodConfigs = [];
          Object.values(this.paymentMethodConfigMap)
            .forEach((pspMethods) => {
              Object.values(pspMethods)
                .forEach(methodConfig => paymentMethodConfigs.push(methodConfig));
            });
          storeConfig.paymentConfiguration.paymentMethodConfiguration = paymentMethodConfigs;
          JSON.stringify(storeConfig);
        } catch (e) {
          this.reportError(e);
          this.showErrorMessage();
        }
        this.axios.put(this.serviceUrls.interfaces.onlineBooking.get(this, this.interfaceId), storeConfig)
          .then((response) => {
            this.showMessages(response);
            cbSaveComplete();
          })
          .catch((error) => {
            if (isNil(error.response)) this.showErrorMessage();
            this.showMessages(error.response);
            cbSaveComplete();
          });
      },
    },
  ],

  data() {
    return {
      config: null,
      stripeConfig: {
        enabled: false,
      },
      mollieConfig: {
        enabled: false,
      },
      fanzConfig: {
        enabled: false,
      },
      paymentMethodConfigMap: {
        Stripe: [],
        Mollie: [],
        Fanz: [],
        Manual: [],
      },
      interfaceId: this.$route.params.interfaceId,
      templates: [],
      genericEmailTemplates: [],
    };
  },

  computed: {
    env: function () {
      return process.env.HOTELMEISTER_ENV;
    },
    enums: function () {
      return this.$store.getters['enums/enums'];
    },
    disabledPaymentServiceProviders: function () {
      const ret = [];

      if (this.stripeConfig.enabled === false) ret.push('Stripe');
      if (this.mollieConfig.enabled === false) ret.push('Mollie');
      if (this.fanzConfig.enabled === false) ret.push('Fanz');

      return ret;
    },
  },

  watch: {
    'config.paymentConfiguration.primaryPaymentServiceProvider': function (newVal) {
      if (isNil(this.paymentMethodConfigMap) || isNil(this.paymentMethodConfigMap[newVal])) return;
      switch (newVal) {
        case 'Fanz':
        case 'Manual':
          Object.values(this.paymentMethodConfigMap[newVal]).forEach((it) => {
            it.active = true;
            it.minDaysBeforeArrival = 0;
          });
          break;
        default:
          // do nothing
          break;
      }
    },
  },

  created: function () {
    this.loadData();
  },

  updated() {
  },

  beforeDestroy() {
  },

  methods: {
    openUrl(url) {
      window.open(url, '_blank');
    },
    loadData() {
      this.axios.get(this.serviceUrls.interfaces.onlineBooking.get(this, null))
        .then((response) => {
          this.config = {
            imprintPageContent: {},
            reservationConfirmedPageContent: {},
            ...response.data.data,
          };

          if (isNil(this.config.cutOffPeriod)) this.config.cutOffPeriod = 'PT0S';
          if (isNil(this.config.imprintPageContent)) this.config.imprintPageContent = {};
          if (isNil(this.config.reservationConfirmedPageContent)) this.config.reservationConfirmedPageContent = {};
          if (isNil(this.config.includingAllTaxesAndFees)) this.config.includingAllTaxesAndFees = true;

          this.enums.PaymentServiceProvider.forEach((psp) => {
            this.config.paymentConfiguration.paymentMethodConfiguration.filter(it => it.paymentServiceProvider === psp).forEach((paymentMethodConfig) => {
              this.$set(
                this.paymentMethodConfigMap[psp],
                paymentMethodConfig.paymentMethod,
                paymentMethodConfig,
              );
            });
          });
          this.enums.PaymentServiceProvider.forEach((psp) => {
            // enums.ADDITIONAL_DATA.PaymentServiceProviderPaymentMethod[paymentMethod].paymentServiceProviders.includes
            this.enums.PaymentServiceProviderPaymentMethod.forEach((paymentMethod) => {
              if (isNil(this.paymentMethodConfigMap[psp][paymentMethod]) && this.enums.ADDITIONAL_DATA.PaymentServiceProviderPaymentMethod[paymentMethod].paymentServiceProviders.includes(psp)) {
                this.$set(
                  this.paymentMethodConfigMap[psp],
                  paymentMethod,
                  {
                    active: false,
                    paymentMethod: paymentMethod,
                    paymentServiceProvider: psp,
                    expiry: null,
                    minDaysBeforeArrival: 0,
                  },
                );
              }
            });
          });
        });
      this.axios.get(this.serviceUrls.interfaces.stripe.get(this, null))
        .then((response) => {
          this.stripeConfig = response.data.data;
        });
      this.axios.get(this.serviceUrls.interfaces.mollie.get(this, null))
        .then((response) => {
          this.mollieConfig = response.data.data;
        });
      this.axios.get(this.serviceUrls.interfaces.fanz.get(this, null))
        .then((response) => {
          this.fanzConfig = response.data.data;
        });
      this.updateTemplates();
    },
    updateTemplates() {
      this.axios.get(this.serviceUrls.templateHelper.filter(this, 'Email'))
        .then((response) => {
          this.templates = response.data.data.filter(it => it.type === 'ReservationConfirmationEmail');
          this.genericEmailTemplates = response.data.data.filter(it => it.type === 'GenericEmail');
        });
    },
    copyCode() {
      this.$copy(this.$refs.code.$el.querySelector('pre').innerText);
      const el = this.$refs.code.$el;
      const tl = new TimelineMax({
        onComplete: () => {
          el.style.boxShadow = null;
        },
      });
      tl.add(TweenMax.fromTo(el, 0.25, { backgroundColor: '#fff' }, { backgroundColor: '#bbdcd8' }));
      tl.add(TweenMax.fromTo(el, 0.5, { backgroundColor: '#bbdcd8' }, { backgroundColor: '#fff' }));
    },
    isProduction() {
      return isProduction;
    },
    createDefaultPaymentRegisteredTemplate(confirm = false) {
      if (confirm === true) {
        this.axios.post(this.serviceUrls.interfaces.onlineBooking.createPaymentRegisteredTemplate(this))
          .then((response) => {
            this.showSuccessMessage();
            this.updateTemplates();
          });
      } else {
        this.showConfirmModal(this.$t('admin.interfaces.pageOnlineBooking.confirmCreateDefaultPaymentRegisteredTemplate'), () => this.createDefaultPaymentRegisteredTemplate(true));
      }
    },
    invalidateCache() {
      this.axios.post(this.serviceUrls.interfaces.onlineBooking.invalidateCache(this))
        .then((response) => {
          this.showSuccessMessage();
          this.showMessages(response);
        })
        .catch((error) => {
          if (isNil(error.response)) this.showErrorMessage();
          this.showMessages(error.response);
        });
    },
    googleUpdateProperty() {
      this.axios.post(this.serviceUrls.interfaces.googleHotel.updateRoomTypesRates(this))
        .then((response) => {
          this.showSuccessMessage();
          this.showMessages(response);
        })
        .catch((error) => {
          if (isNil(error.response)) this.showErrorMessage();
          this.showMessages(error.response);
        });
    },
    googleUpdateTaxesFees() {
      this.axios.post(this.serviceUrls.interfaces.googleHotel.updateTaxesFees(this))
        .then((response) => {
          this.showSuccessMessage();
          this.showMessages(response);
        })
        .catch((error) => {
          if (isNil(error.response)) this.showErrorMessage();
          this.showMessages(error.response);
        });
    },
    updateAllRatesAndAvailabilities() {
      this.axios.post(this.serviceUrls.interfaces.googleHotel.updateAllRatesAndAvailabilities(this))
        .then((response) => {
          this.showSuccessMessage();
          this.showMessages(response);
        })
        .catch((error) => {
          if (isNil(error.response)) this.showErrorMessage();
          this.showMessages(error.response);
        });
    },
    googleHotelXml(all = false) {
      this.axios.post(this.serviceUrls.interfaces.googleHotel.googleHotelXml(this), null, { params: { all: all ? '1' : null } })
        .then((response) => {
          this.showSuccessMessage();
          const xmlContent = response.data; // XML string
          const blob = new Blob([xmlContent], { type: 'text/xml' });
          const url = URL.createObjectURL(blob);
          // window.open(url, '_blank');

          const a = document.createElement('a');
          a.href = url;
          a.download = 'google_hotel_listings.xml'; // filename for download
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);

          URL.revokeObjectURL(url); // cleanup
        })
        .catch((error) => {
          if (isNil(error.response)) this.showErrorMessage();
        });
    },
  },
};
</script>

<!-- Scoped SCSS  -->
<style lang="scss" scoped>
</style>

<style lang="scss">

</style>



// WEBPACK FOOTER //
// src/components/pages/admin/interfaces/PageOnlineBooking.vue